import React, { FC, useEffect, useRef } from 'react';
import Modal from 'components/shared/Modal';
import AutomaticForm from 'components/modals/newSocialMediaProfile/automaticForm/Form';
import { ModalRouteProps } from 'context/ModalRouter';
import { useTranslation } from 'react-i18next';
import { NumberParam, useQueryParams } from 'use-query-params';
import { ActivityIndicator } from '@kontentino/ui';
import useAddNewProfileAutomatically from 'app/modules/posts/hooks/useAddNewProfileAutomatically';
import { useSocialLoginContext } from 'app/modules/socialLogin/context';
import { usePostsContainer } from 'components/posts/context';
import HeapUtils from 'app/utils/heap';
import { connectTracking } from 'app/modules/socialLogin/utils/connectTracking';
import { NewAddedPage } from 'types/Page';

type Props = ModalRouteProps & {
  onProfileAdded?: (profileId: number) => void;
};

export const AssociateProfileModal: FC<Props> = ({
  modalManager,
  modalHistory,
  onProfileAdded,
}) => {
  const { t } = useTranslation();
  const socialLogin = useSocialLoginContext();
  const [query] = useQueryParams({
    type: NumberParam,
    id: NumberParam,
  });
  const automaticPageAdded = useAddNewProfileAutomatically({
    onProfileAdded,
  });
  const hasSavedProfile = useRef(false);

  const handlePageAdded = (addedPage: NewAddedPage) => {
    hasSavedProfile.current = true;
    automaticPageAdded.onPageAdded(addedPage);
  };

  useEffect(() => {
    return () => {
      const attempt = connectTracking.get();

      if (attempt?.source === 'add_profile') {
        if (!hasSavedProfile.current) {
          HeapUtils.track(HeapUtils.events.CONNECT_FLOW_CLOSED, {
            network: attempt.network,
            last_step: 'select-profiles',
          });
        }
        connectTracking.clear();
      }
      socialLogin.reset();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Modal
      modalManager={modalManager}
      size="sm"
      dataName={{
        wrapper: 'associate-social-media-profile-modal',
      }}
    >
      <Modal.Header title={t('associateProfileTitle')} />
      <Modal.Content className="tw-p-0">
        {query.type && (
          <AutomaticForm
            {...socialLogin}
            pageType={query.type}
            pageId={query.id ?? undefined}
            onPageAdded={handlePageAdded}
            onGoBack={modalHistory.closeLast}
          />
        )}
        {!query.type && (
          <div className="tw-flex tw-items-center tw-justify-center tw-p-10">
            <ActivityIndicator className="tw-h-7 tw-w-7" />
          </div>
        )}
      </Modal.Content>
    </Modal>
  );
};

const PostsAssociateProfileModal: FC<ModalRouteProps> = (props) => {
  const postsContainer = usePostsContainer();

  return (
    <AssociateProfileModal
      {...props}
      onProfileAdded={(id) =>
        postsContainer.changeFilter({
          key: 'selectedProfiles',
          value: [id],
        })
      }
    />
  );
};

export default PostsAssociateProfileModal;
